import React, { useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';

import TableRowAction from 'components/common/TableRowAction';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import ViewFileButton from 'components/common/ViewFileButton';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';

import { useAuthContext } from 'context/authContext';
import useUpload from 'hooks/useUpload';
import {
  useDonorDocuments,
  useCreateDonorDocument,
  useDeleteDonorDocument
} from 'hooks/useDonors';

import DonorDocumentUploadModal from './DonorDocumentUploadModal';

const formatUserName = user =>
  user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '---';

const formatSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Files kept against a donor: Direct Debit mandates, standing order forms,
// Gift Aid declarations, proof of ID and so on
const DonorDocuments = () => {
  const { id } = useParams();
  const { user } = useAuthContext();

  const { documents, isLoading } = useDonorDocuments(id);
  const { createDocumentAsync, isCreating } = useCreateDonorDocument();
  const { deleteDocumentAsync } = useDeleteDonorDocument();
  const { uploadDocument, loading: isUploading } = useUpload();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const columns = useMemo(
    () => [
      {
        accessor: 'document_type',
        Header: 'Document Type',
        cellProps: { className: 'py-2' },
        Cell: rowData => {
          const row = rowData.row.original;
          return (
            <div>
              <span style={{ fontSize: '1rem' }}>{row.document_type}</span>
              {row.notes && (
                <div className="text-muted fs--1 mt-1">{row.notes}</div>
              )}
            </div>
          );
        }
      },
      {
        accessor: 'file_name',
        Header: 'File',
        cellProps: { className: 'py-2' },
        Cell: rowData => {
          const row = rowData.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              <ViewFileButton url={row.file_url} />
              <span className="text-muted fs--1">
                {row.file_name}
                {row.file_size ? ` (${formatSize(row.file_size)})` : ''}
              </span>
            </div>
          );
        }
      },
      {
        accessor: 'uploaded_by_user',
        Header: 'Uploaded By',
        cellProps: { className: 'py-2' },
        Cell: rowData => (
          <span style={{ fontSize: '1rem' }}>
            {formatUserName(rowData.row.original.uploaded_by_user)}
          </span>
        )
      },
      {
        accessor: 'created_at',
        Header: 'Uploaded',
        cellProps: { className: 'py-2' },
        Cell: rowData => (
          <span style={{ fontSize: '1rem' }}>
            {format(
              new Date(rowData.row.original.created_at),
              'dd-MM-yyyy, hh:mm a'
            )}
          </span>
        )
      },
      {
        accessor: 'id',
        Header: '',
        cellProps: { className: 'py-2 text-end' },
        disableSortBy: true,
        Cell: rowData => (
          <TableRowAction
            onCLickDelete={() => setConfirmDeleteId(rowData.row.original.id)}
            subject={permissionsKeyMap.donor.children.donors}
          />
        )
      }
    ],
    []
  );

  const handleUpload = async ({ documentType, notes, file }) => {
    try {
      const fileUrl = await uploadDocument(file, 'donor-documents');
      await createDocumentAsync({
        donor_id: id,
        document_type: documentType,
        notes,
        file_url: fileUrl,
        file_name: file.name,
        file_size: file.size,
        uploaded_by: user?.id || null
      });
      setShowUploadModal(false);
    } catch (e) {
      // toast handled in hooks
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDocumentAsync(confirmDeleteId);
      setConfirmDeleteId(null);
    } catch (e) {
      // toast handled in hook
    }
  };

  return (
    <>
      {showUploadModal && (
        <DonorDocumentUploadModal
          show={showUploadModal}
          onHide={() => setShowUploadModal(false)}
          onSubmit={handleUpload}
          loading={isCreating || isUploading}
        />
      )}
      {confirmDeleteId && (
        <ConfirmationModal
          show={!!confirmDeleteId}
          handleConfirm={handleConfirmDelete}
          onHide={() => setConfirmDeleteId(null)}
          title="Delete Document"
          body="Are you sure you want to delete this document?"
        />
      )}

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={documents}
            columns={columns}
            title="Documents"
            showSearchInput
            searchInputPlaceHolder="Search"
            addNew
            addButtonLabel="Upload Document"
            onClickAddNew={() => setShowUploadModal(true)}
            perPage={50}
            loading={isLoading}
            subject={permissionsKeyMap.donor.children.donors}
          />
        </Col>
      </Row>
    </>
  );
};

export default DonorDocuments;
